import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
var _excluded = ["sticky", "__scopeDialog"],
  _excluded2 = ["size", "swipeToDismissEnaled", "overlay", "className", "onAnimationEnd", "onAnimationStart", "forceMount", "children", "overlayProps", "portalProps", "style"],
  _excluded3 = ["className"],
  _excluded4 = ["container"],
  _excluded5 = ["isDragging", "transform3DValues"],
  _excluded6 = ["__scopeDialog"],
  _excluded7 = ["__scopeDialog"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/* eslint-disable max-lines */
import { clsx } from 'clsx';
import * as React from 'react';
import { RoundButton } from '../../../button';
import { DialogClose, DialogContent, DialogDescription, DialogOverlay, DialogPortal, DialogRoot, DialogTitle, DialogTrigger } from '../../../headless';
import { DomPrimitive } from '../../../internal/components/DomPrimitive';
import { createContext } from '../../../internal/context/scoped-context';
import { useComposedRefs } from '../../../internal/hooks/useComposedRefs';
import { composeEventHandlers } from '../../../internal/utils/composeEventHandlers';
import { useDeviceType } from '../../../responsive';
import { ArticleHeading1, ArticleHeading2, ArticleParagraph1 } from '../../../typography';
import { useSwipeToDismiss } from '../../internal/hooks/useSwipeToDismiss';
import * as styles from './Modal.module.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var CLOSE_BUTTON_NAME = 'ModalCloseButton';
var CONTENT_NAME = 'ModalContent';
var TITLE_NAME = 'ModalTitle';
var TRIGGER_NAME = 'ModalTrigger';
var DESCRIPTION_NAME = 'ModalTitle';
var HEADER_NAME = 'ModalHeader';
var FOOTER_NAME = 'ModalFooter';
var BODY_NAME = 'ModalBody';
var _createContext = createContext('Modal'),
  _createContext2 = _slicedToArray(_createContext, 2),
  ModalContextProvider = _createContext2[0],
  useModalContext = _createContext2[1];

/**
 * Компонент для рендера кнопки открытия модального окна
 */

export var ModalTrigger = /*#__PURE__*/React.forwardRef(function (props, ref) {
  return /*#__PURE__*/_jsx(DialogTrigger, _objectSpread(_objectSpread({}, props), {}, {
    ref: ref
  }));
});
ModalTrigger.displayName = TRIGGER_NAME;

/**
 * Компонент для рендера кнопки закрытия модального окна
 */

export var ModalClose = DialogClose;

/**
 * Компонент для рендера плавающей кнопки закрытия модального окна
 */

export var ModalCloseButton = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var _ref$sticky = _ref.sticky,
    sticky = _ref$sticky === void 0 ? true : _ref$sticky,
    __scopeDialog = _ref.__scopeDialog,
    props = _objectWithoutProperties(_ref, _excluded);
  return /*#__PURE__*/_jsx(DomPrimitive.div, {
    className: clsx(styles['close'], sticky && styles['close-sticky']),
    children: /*#__PURE__*/_jsx(DomPrimitive.div, {
      className: styles['close-inner'],
      children: /*#__PURE__*/_jsx(DialogClose, {
        __scopeDialog: __scopeDialog,
        asChild: true,
        children: /*#__PURE__*/_jsx(RoundButton, _objectSpread(_objectSpread({}, props), {}, {
          ref: ref
        }))
      })
    })
  });
});
ModalCloseButton.displayName = CLOSE_BUTTON_NAME;

/**
 * Компонент для рендера контекста модального окна
 */

export function ModalRoot(props) {
  return /*#__PURE__*/_jsx(ModalContextProvider, _objectSpread(_objectSpread({}, props), {}, {
    children: /*#__PURE__*/_jsx(DialogRoot, _objectSpread({}, props))
  }));
}

/**
 * Компонент для рендера контента модального окна
 */
var DEFAULT_OVERLAY_PROPS = {};
var DEFAULT_PORTAL_PROPS = {};
export var ModalContent = /*#__PURE__*/React.forwardRef(function (_ref2, forwardedRef) {
  var _ref2$size = _ref2.size,
    size = _ref2$size === void 0 ? 'M' : _ref2$size,
    _ref2$swipeToDismissE = _ref2.swipeToDismissEnaled,
    swipeToDismissEnaled = _ref2$swipeToDismissE === void 0 ? true : _ref2$swipeToDismissE,
    overlay = _ref2.overlay,
    className = _ref2.className,
    onAnimationEnd = _ref2.onAnimationEnd,
    onAnimationStart = _ref2.onAnimationStart,
    forceMount = _ref2.forceMount,
    children = _ref2.children,
    overlayProps = _ref2.overlayProps,
    portalProps = _ref2.portalProps,
    contentStyles = _ref2.style,
    restContentProps = _objectWithoutProperties(_ref2, _excluded2);
  var _ref3 = overlayProps || DEFAULT_OVERLAY_PROPS,
    overlayClassName = _ref3.className,
    restOverlayProps = _objectWithoutProperties(_ref3, _excluded3);
  var _ref4 = portalProps || DEFAULT_PORTAL_PROPS,
    portalContainer = _ref4.container,
    restPortalProps = _objectWithoutProperties(_ref4, _excluded4);
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    isAnimating = _React$useState2[0],
    setAnimating = _React$useState2[1];
  var contentRef = React.useRef(null);
  var ref = useComposedRefs(forwardedRef, contentRef);
  var context = useModalContext(CONTENT_NAME);
  var deviceType = useDeviceType();
  var _useSwipeToDismiss = useSwipeToDismiss({
      onDismiss: function onDismiss(transformValues) {
        var _contentRef$current, _context$onOpenChange;
        var translateY = transformValues[1];

        /**
         * Устанавливаем значение --initial-translate, чтобы анимация slide-down
         * для закрытия мобильной модалки отработала корректно, относительно текущего translateY
         */
        (_contentRef$current = contentRef.current) === null || _contentRef$current === void 0 || _contentRef$current.style.setProperty('--initial-translate', typeof translateY === 'number' ? "".concat(translateY, "px") : translateY);
        (_context$onOpenChange = context.onOpenChange) === null || _context$onOpenChange === void 0 || _context$onOpenChange.call(context, false);
      },
      enabled: swipeToDismissEnaled && deviceType === 'phone',
      transformElementRef: contentRef
    }),
    isDragging = _useSwipeToDismiss.isDragging,
    transform3DValues = _useSwipeToDismiss.transform3DValues,
    eventListeners = _objectWithoutProperties(_useSwipeToDismiss, _excluded5);
  var style = React.useMemo(function () {
    if (isDragging) {
      return _objectSpread(_objectSpread({}, contentStyles), {}, {
        transform: "translate3d(".concat(transform3DValues.join(','), ")")
      });
    }
    return contentStyles;
  }, [contentStyles, isDragging, transform3DValues]);

  /**
   * На моб устройствах используем css анимацию transofrmY (slide down и slide up)
   * чтобы не было мерцания скролбара при открытии/закрытии модалки
   * добавляем класс устанавливающий overflow: hidden на wrapper во время анимации
   */
  var handleAnimationStart = React.useMemo(function () {
    return composeEventHandlers(onAnimationStart, function () {
      return setAnimating(true);
    });
  }, [onAnimationStart]);
  var handleAnimationEnd = React.useMemo(function () {
    return composeEventHandlers(onAnimationEnd, function () {
      var _contentRef$current2;
      setAnimating(false);
      /**
       * Сбрасываем переменную значение --initial-translate для анимации закрытия мобильной модалки
       */
      (_contentRef$current2 = contentRef.current) === null || _contentRef$current2 === void 0 || _contentRef$current2.style.setProperty('--initial-translate', '0');
    });
  }, [onAnimationEnd]);
  return /*#__PURE__*/_jsx(DialogPortal, _objectSpread(_objectSpread({
    container: portalContainer,
    forceMount: restOverlayProps.forceMount || forceMount
  }, restPortalProps), {}, {
    children: /*#__PURE__*/_jsx(DialogOverlay, _objectSpread(_objectSpread({
      className: clsx(overlayClassName, styles['overlay'], overlay === 'transparent' && styles['overlay-transparent'], overlay === 'blured' && styles['overlay-blured'])
    }, restOverlayProps), {}, {
      children: /*#__PURE__*/_jsx("div", {
        className: clsx(styles['content-wrapper'], (isAnimating || isDragging) && styles['content-wrapper-animating']),
        children: /*#__PURE__*/_jsx("div", {
          className: styles['content-inner'],
          children: /*#__PURE__*/_jsxs(DialogContent, _objectSpread(_objectSpread({
            ref: ref,
            forceMount: forceMount,
            onAnimationStart: handleAnimationStart,
            onAnimationEnd: handleAnimationEnd,
            className: clsx(styles['content'], styles["content-".concat(size.toLocaleLowerCase())], className),
            style: style
          }, restContentProps), {}, {
            children: [deviceType === 'phone' && /*#__PURE__*/_jsx(DomPrimitive.div, _objectSpread({
              className: clsx(styles['swipe-handle'])
            }, eventListeners)), children]
          }))
        })
      })
    }))
  }));
});
ModalContent.displayName = CONTENT_NAME;

/**
 * Компонент для рендера заголовка модального окна
 */

export var ModalTitle = /*#__PURE__*/React.forwardRef(function (props, forwardedRef) {
  var deviceType = useDeviceType();
  var __scopeDialog = props.__scopeDialog,
    rest = _objectWithoutProperties(props, _excluded6);
  var Heading = React.useMemo(function () {
    switch (deviceType) {
      case 'phone':
        return ArticleHeading2;
      default:
        return ArticleHeading1;
    }
  }, [deviceType]);
  return /*#__PURE__*/_jsx(DomPrimitive.div, {
    className: styles['title'],
    children: /*#__PURE__*/_jsx(DialogTitle, {
      __scopeDialog: __scopeDialog,
      asChild: true,
      children: /*#__PURE__*/_jsx(Heading, _objectSpread(_objectSpread({}, rest), {}, {
        ref: forwardedRef
      }))
    })
  });
});
ModalTitle.displayName = TITLE_NAME;

/**
 * Компонент для рендера описания модального окна
 * описание может использоваться как в заголовке так и в теле модального окна
 */

export var ModalDescription = /*#__PURE__*/React.forwardRef(function (_ref5, forwardedRef) {
  var __scopeDialog = _ref5.__scopeDialog,
    props = _objectWithoutProperties(_ref5, _excluded7);
  return /*#__PURE__*/_jsx(DomPrimitive.div, {
    className: styles['description'],
    children: /*#__PURE__*/_jsx(DialogDescription, {
      __scopeDialog: __scopeDialog,
      asChild: true,
      children: /*#__PURE__*/_jsx(ArticleParagraph1, _objectSpread(_objectSpread({}, props), {}, {
        ref: forwardedRef
      }))
    })
  });
});
ModalDescription.displayName = DESCRIPTION_NAME;

/**
 * Компонент для рендера контейнера заголовка модального окна
 */

export var ModalHeader = /*#__PURE__*/React.forwardRef(function (_ref6, forwardedRef) {
  var children = _ref6.children,
    _ref6$stycky = _ref6.stycky,
    stycky = _ref6$stycky === void 0 ? true : _ref6$stycky;
  return /*#__PURE__*/_jsx(DomPrimitive.div, {
    ref: forwardedRef,
    className: clsx(styles['header'], stycky && styles['header-sticky']),
    children: children
  });
});
ModalHeader.displayName = HEADER_NAME;

/**
 * Компонент для рендера контейнера футера модального окна
 */

export var ModalFooter = /*#__PURE__*/React.forwardRef(function (_ref7, forwardedRef) {
  var children = _ref7.children,
    _ref7$sticky = _ref7.sticky,
    sticky = _ref7$sticky === void 0 ? true : _ref7$sticky;
  return /*#__PURE__*/_jsx(DomPrimitive.div, {
    ref: forwardedRef,
    className: clsx(styles['footer'], sticky && styles['footer-sticky']),
    children: children
  });
});
ModalFooter.displayName = FOOTER_NAME;

/**
 * Компонент контейнер тела модального окна
 */

export var ModalBody = /*#__PURE__*/React.forwardRef(function (_ref8, forwardedRef) {
  var children = _ref8.children;
  return /*#__PURE__*/_jsx(DomPrimitive.div, {
    ref: forwardedRef,
    className: styles['body'],
    children: children
  });
});
ModalBody.displayName = BODY_NAME;
export { ModalBody as Body, ModalClose as Close, ModalCloseButton as CloseButton, ModalContent as Content, ModalDescription as Description, ModalFooter as Footer, ModalHeader as Header, ModalRoot as Root, ModalTitle as Title, ModalTrigger as Trigger };